import React from 'react';
import PropTypes from 'prop-types';

import { Grid } from '@mui/material';

import { useConsult } from 'shared/context/ConsultContext';
import { useFormState } from 'shared/hooks';
import useConsultData from 'features/consults/hooks/useConsultData';
import useConsultInitialFormState from 'features/consults/hooks/useConsultInitialFormState';

import { Box } from 'styles/layout';
import GridWrapper from 'shared/components/layout/GridWrapper/GridWrapper';

import ConsultLock from 'features/consults/components/ConsultLock/ConsultLock';
import QuestionsPanel from 'features/consults/components/tabFields/QuestionsPanel';
import Autocomplete from 'shared/components/forms/Autocomplete';
import formFields from 'features/consults/shared/tabs/Signature/formFields';
import { neuroSignatureSchema } from 'features/consults/shared/tabs/Signature/validators';
import QualityReviewPanel from 'features/consults/shared/tabs/Signature/QualityReviewPanel';

import { CONSULT_TABS } from 'features/consults/utils/constants';

import { neuroSignatureRequiredFields } from 'features/consults/utils/validators';
import CheckboxField from 'shared/components/forms/Checkbox/CheckboxField';

import { PermissionEnum } from 'utils/permissions';
import usePermission from 'shared/hooks/usePermission';

import leftPanel from './data';

const SignatureNeuro = ({ errors }) => {
  const {
    register,
    control,
    getValues,
    getFieldError,
    setFormValues,
    clearErrors,
    trigger,
    watch,
  } = useFormState(neuroSignatureSchema, {
    stateSlice: 'consults',
    showAsSnackbar: true,
  });
  const {
    consult,
    consultTabData,
    isClosed,
    isEditable,
    isMacro,
    handleFieldChange,
    handleOwnerChange,
    handleConsultLock,
    saveConsultChange,
  } = useConsultData(CONSULT_TABS.SIGNATURE, getValues);

  const { consultProviders } = useConsult();

  const hasPaocPermission = usePermission(PermissionEnum.HasPaocPermission);
  const currentAttestationValue = watch(
    formFields.attestationOfConsultCompletion.name,
  );

  const requiredFields = React.useMemo(
    () => neuroSignatureRequiredFields(consult),
    [consult],
  );

  const updatedLeftPanel = React.useMemo(
    () => ({
      ...leftPanel,
      elements: leftPanel.elements.map((field) => ({
        ...field,
        required: requiredFields.some(
          (reqField) => reqField.name === field.name,
        ),
      })),
    }),
    [requiredFields],
  );

  useConsultInitialFormState(consult.id, consultTabData, setFormValues);

  React.useEffect(() => {
    clearErrors();
    if (errors) trigger(errors);
  }, [errors, clearErrors, trigger]);

  return (
    <GridWrapper>
      <Grid item sm={12} lg={6}>
        <QuestionsPanel
          {...updatedLeftPanel}
          control={control}
          register={register}
          onChange={handleFieldChange}
          disabled={!isEditable}
          getFieldError={getFieldError}
          disableElements={
            isMacro
              ? [
                  formFields.attestationOfConsultCompletion.name,
                  formFields.consent.name,
                  formFields.totalFaceToFaceTime.name,
                  formFields.criticalCareTime.name,
                ]
              : []
          }
        />
      </Grid>
      <Grid item sm={12} lg={6}>
        <Box mb={1}>
          <Autocomplete
            control={control}
            name="assignToId"
            label="Neurologist"
            required
            options={consultProviders}
            disabled={isMacro || isClosed}
            handleChange={handleOwnerChange}
          />
        </Box>
        <Box mb={1}>
          <Autocomplete
            control={control}
            name="backupNeurologistId"
            label="Are you providing backup for on-call provider? If so select on-call below:"
            options={consultProviders}
            disabled={isMacro || !isEditable}
            handleChange={() => handleFieldChange('backupNeurologistId')}
          />
        </Box>
        {!isMacro && (
          <ConsultLock
            consult={consult}
            attestationValue={currentAttestationValue}
            disabled={!isEditable}
            checked={isClosed}
            onConsultLock={handleConsultLock}
          />
        )}
        <QualityReviewPanel
          form={{
            control,
            register,
            watch,
            getValues,
            setFormValues,
            getFieldError,
          }}
          withOptions
          onChange={saveConsultChange}
          onFieldChange={handleFieldChange}
          disabled={isMacro || !isEditable}
        />
        {hasPaocPermission && (
          <CheckboxField
            control={control}
            name={formFields.completedByPaoc.name}
            label={formFields.completedByPaoc.label}
            disabled={isMacro || !isEditable}
            onChange={() => handleFieldChange(formFields.completedByPaoc.name)}
          />
        )}
      </Grid>
    </GridWrapper>
  );
};

SignatureNeuro.propTypes = {
  errors: PropTypes.arrayOf(PropTypes.string),
};

export default SignatureNeuro;
